import router from '@/router'
import IqProApi from '@/apis/IqProApi'

import { State, Terminal, TransactionDetails } from './types/state'
import { InjectionKey } from 'vue'
import { createStore, useStore as baseUseStore, Store } from 'vuex'
import * as Sentry from '@sentry/vue'
import * as mp from './apis/mixpanel'
import { GroupProperty } from './types/mixpanel'

let url = ''

if (process.env.NODE_ENV === 'development') {
	url = 'https://sandbox.basysiqpro.com' // for local dev
} else {
	url = 'https://' + process.env.VUE_APP_FLUIDPAY_ENV + '.basysiqpro.com'
}

export const key: InjectionKey<Store<State>> = Symbol()
export const store = createStore<State>({
	state: {
		distinctId: '',
		simplePayData: [],
		username: '',
		baseUrl: url,
		isLoading: true,
		loginToken: '',
		isLoggedIn: false,
		simplePayId: null as string,
		simplePayUserView: '',
		isCardholderSurchargeable: false,
		isStateless: false,
		iqMerchantData: {
			simplePaySettings: {
				cardFields: {},
				achFields: {}
			}
		},
		fpMerchantData: {
			publicApiKey: '',
			iqProReqFields: [],
			cardCustomFields: [],
			achCustomFields: [],
			vaultCustomFieldCard: [],
			vaultCustomFieldAch: [],
			settings: {}
		},
		transactionData: {
			responseCode: null as number,
			authCode: null as number,
			confirmationNumber: null as number,
			timestamp: null as string,
			transactionAmount: null as number
		},
		locales: {
			countryList: [],
			stateList: []
		},
		logos: {
			iqProLogoNavy:
				'data:image/png;base64,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'
		}
	},
	mutations: {
		isStateless(state: State, payload) {
			state.isStateless = payload
		},
		countries(state: State, payload: any) {
			state.locales.countryList = payload
		},
		states(state: State, payload: any) {
			state.locales.stateList = payload
		},
		username(state: State, payload: any) {
			state.username = payload
		},
		distinctId(state: State, payload: any) {
			state.distinctId = payload
		},
		simplePayData(state: State, payload: any) {
			state.simplePayData = payload
		},
		isLoading(state: State, payload: any) {
			state.isLoading = payload
		},
		login(state: State, payload: any) {
			state.isLoggedIn = true
			state.loginToken = payload
		},
		logout(state: State) {
			state.loginToken = null
			state.isLoggedIn = false
		},
		simplePayId(state: State, payload: any) {
			state.simplePayId = payload
		},
		simplePayUserView(state: State, payload: any) {
			state.simplePayUserView = payload
		},
		responseCode(state: State, payload: any) {
			state.transactionData.responseCode = payload
		},
		authCode(state: State, payload: any) {
			state.transactionData.authCode = payload
		},
		confirmationNumber(state: State, payload: any) {
			state.transactionData.confirmationNumber = payload
		},
		timestamp(state: State, payload: any) {
			state.transactionData.timestamp = payload
		},
		transactionAmount(state: State, payload: any) {
			state.transactionData.transactionAmount = payload
		},
		showBanner(state: State, payload: any) {
			state.iqMerchantData.simplePaySettings.hasBanner = true
			state.iqMerchantData.simplePaySettings.bannerType =
				payload.bannerType
			state.iqMerchantData.simplePaySettings.bannerMessage =
				payload.bannerMessage
		},
		clearBanner(state: State) {
			state.iqMerchantData.simplePaySettings.hasBanner = false
			state.iqMerchantData.simplePaySettings.bannerType = 'warning'
			state.iqMerchantData.simplePaySettings.bannerMessage = ''
		},
		clearStateData(state: State) {
			state.locales.stateList = []
		},
		iqMerchantSettings(state: State, apiPayload: any) {
			const payload = JSON.parse(JSON.stringify(apiPayload))

			// If values are set, use them. Otherwise pass a default value.
			// This helps prevent the application from throwing an error if an adjustment is made and the database entries aren't updated yet.
			const settingsWithDefaults = {
				hasCardForm: payload.hasCardForm ?? true,
				hasAchForm: payload.hasAchForm ?? false,
				achProcessing: payload.achProcessing ?? 'ccd',
				isSurchargingEnabled: false,
				isActive: payload.isActive ?? true,
				acceptedCards: payload.acceptedCards ?? [
					'Visa',
					'MasterCard',
					'Discover',
					'American Express'
				],
				merchantName: payload.merchantName ?? '',
				primaryColor: payload.primaryColor
					? payload.primaryColor
					: '#0d304c',
				secondaryColor: payload.secondaryColor ?? '',
				enlargeLogo: payload.enlargeLogo ?? false,
				bodyFont: payload.bodyFont ?? 'Avenir',
				headerFont: payload.headerFont ?? 'Roboto Slab',
				bodyFontColor: payload.bodyFontColor ?? '#6d6c6b',
				backgroundColor: payload.backgroundColor ?? '#21669e',
				headerFontColor: payload.headerFontColor ?? '#6d6c6b',
				formBackgroundColor: payload.formBackgroundColor ?? '#fff',
				logo: payload.logo ?? '',
				headerText: payload.headerText ?? '',
				hasBanner: payload.hasBanner ?? false,
				bannerType: payload.bannerType ?? 'warning',
				bannerMessage: payload.bannerMessage ?? '',
				successMessage: payload.successMessage ?? '',
				declineMessage: payload.declineMessage ?? '',
				successOverrideUrl: payload.successOverrideUrl ?? '',
				backToHomeOverrideUrl: payload.backToHomeOverrideUrl ?? '',
				webHookUrl: payload.webHookUrl ?? '',
				achProcessorId: payload.achProcessorId ?? '',
				achAdjType: '',
				achAdjRate: 0,
				cardProcessorId: payload.cardProcessorId ?? '',
				cardAdjType: '',
				cardAdjRate: 0,
				cardFields: {
					additionalInfoHeaderText:
						payload.cardFields.additionalInfoHeaderText ??
						'Additional Information',
					companyIsIncluded:
						payload.cardFields.companyIsIncluded ?? true,
					companyIsRequired:
						payload.cardFields.companyIsRequired ?? false,
					companyLabelText:
						payload.cardFields.companyLabelText ?? 'Company',
					addressIsIncluded:
						payload.cardFields.addressIsIncluded ?? true,
					addressIsRequired:
						payload.cardFields.addressIsRequired ?? true,
					emailIsIncluded: payload.cardFields.emailIsIncluded ?? true,
					emailIsRequired: payload.cardFields.emailIsRequired ?? true,
					emailIsRequiredOverride: false,
					phoneIsIncluded: payload.cardFields.phoneIsIncluded ?? true,
					phoneIsRequired: payload.cardFields.phoneIsRequired ?? true,
					phoneIsRequiredOverride: false,
					orderIdIsIncluded:
						payload.cardFields.orderIdIsIncluded ?? true,
					orderIdIsRequired:
						payload.cardFields.orderIdIsRequired ?? false,
					orderIdLabelText:
						payload.cardFields.orderIdLabelText ?? 'Order ID',
					poNumberIsIncluded:
						payload.cardFields.poNumberIsIncluded ?? true,
					poNumberIsRequired:
						payload.cardFields.poNumberIsRequired ?? false,
					poNumberLabelText:
						payload.cardFields.poNumberLabelText ?? 'PO Number',
					noteIsIncluded: payload.cardFields.noteIsIncluded ?? true
				},
				achFields: {
					additionalInfoHeaderText:
						payload.achFields.additionalInfoHeaderText ??
						'Additional Information',
					emailIsIncluded: payload.achFields.emailIsIncluded ?? true,
					emailIsRequired: payload.achFields.emailIsRequired ?? true,
					companyIsIncluded:
						payload.achFields.companyIsIncluded ?? true,
					companyIsRequired:
						payload.achFields.companyIsRequired ?? false,
					companyLabelText:
						payload.achFields.companyLabelText ?? 'Company',
					orderIdIsIncluded:
						payload.achFields.orderIdIsIncluded ?? true,
					orderIdIsRequired:
						payload.achFields.orderIdIsRequired ?? false,
					orderIdLabelText:
						payload.achFields.orderIdLabelText ?? 'Order ID',
					poNumberIsIncluded:
						payload.achFields.poNumberIsIncluded ?? true,
					poNumberIsRequired:
						payload.achFields.poNumberIsRequired ?? false,
					poNumberLabelText:
						payload.achFields.poNumberLabelText ?? 'PO Number',
					noteIsIncluded: payload.achFields.noteIsIncluded ?? true,
					driversLicenseIsIncluded:
						payload.achFields.driversLicenseIsIncluded ?? false,
					dobSSNIsIncluded:
						payload.achFields.dobSSNIsIncluded ?? false
				}
			}

			state.iqMerchantData.simplePaySettings = settingsWithDefaults
		},
		fpMerchantSettings(state: State, payload: any) {
			state.fpMerchantData.settings = payload
		},
		merchantName(state: State, payload: any) {
			state.iqMerchantData.simplePaySettings.merchantName = payload
		},
		isCardholderSurchargeable(state: State, payload: boolean) {
			state.isCardholderSurchargeable = payload
		},
		isSurchargingEnabled(state: State, payload: boolean) {
			state.iqMerchantData.simplePaySettings.isSurchargingEnabled =
				payload
		},
		paymentAdjustmentsCard(state: State, payload: any) {
			state.iqMerchantData.simplePaySettings.cardAdjType = payload.adjType
			state.iqMerchantData.simplePaySettings.cardAdjRate = payload.adjRate
		},
		paymentAdjustmentsAch(state: State, payload: any) {
			state.iqMerchantData.simplePaySettings.achAdjType = payload.adjType
			state.iqMerchantData.simplePaySettings.achAdjRate = payload.adjRate
		},
		achDlAndIdSettings(state: State, payload: any) {
			state.iqMerchantData.simplePaySettings.achFields.driversLicenseIsIncluded =
				payload.dlReq
			state.iqMerchantData.simplePaySettings.achFields.dobSSNIsIncluded =
				payload.idReq
		},
		emailRequiredOverrideCard(state, payload) {
			state.iqMerchantData.simplePaySettings.cardFields.emailIsRequiredOverride =
				payload
		},
		phoneRequiredOverrideCard(state, payload) {
			state.iqMerchantData.simplePaySettings.cardFields.phoneIsRequiredOverride =
				payload
		},
		publicApiKey(state: State, payload: any) {
			state.fpMerchantData.publicApiKey = payload
		},
		iqProReqFields(state: State, payload: any) {
			state.fpMerchantData.iqProReqFields = payload
		},
		cardCustomFields(state: State, payload: any) {
			state.fpMerchantData.cardCustomFields = payload
		},
		achCustomFields(state: State, payload: any) {
			state.fpMerchantData.achCustomFields = payload
		},
		vaultCustomFieldCard(state: State, payload: any) {
			state.fpMerchantData.vaultCustomFieldCard = payload
		},
		vaultCustomFieldAch(state: State, payload: any) {
			state.fpMerchantData.vaultCustomFieldAch = payload
		}
	},
	actions: {
		// action does logic and commit the mutation
		async setUserName({ commit }: any, username: string) {
			commit('username', username)
		},
		async setDistinctId({ commit }: any, distinctId: string) {
			commit('distinctId', distinctId)
		},
		async getCountries({ commit }: any, simplepayId: string) {
			try {
				const resp = await IqProApi.getCountries(simplepayId)
				commit('countries', resp.data.data)
			} catch (ex) {
				Sentry.captureException(ex)
				throw new Error('Failed to get countries')
			}
		},
		async getStates({ commit }: any, payload: any) {
			commit('clearStateData')
			try {
				const resp = await IqProApi.getStates(payload)
				commit('states', resp.data.data.states)
			} catch (ex) {
				commit('clearStateData')
				Sentry.captureException(ex)
				throw new Error('Failed to get states')
			}
		},
		async getSimplePayData({ commit, getters }: any, simplePayId: string) {
			const resp = await IqProApi.getAllSimplePay()
			const selectedPage = getters.simplePayPage(simplePayId)

			if (resp && resp.data) {
				commit('simplePayData', resp.data)

				mp.adminListViewCountPeopleProp()
				mp.adminPageViewTrack()
			} else {
				console.log('getSimplePayData: No data')

				Sentry.withScope(function (scope) {
					scope.setTag('simplepayid page', selectedPage)

					Sentry.captureMessage('getSimplePayData: No data')
				})
			}
		},
		async toggleSimplePayActive(
			{ dispatch, getters }: any,
			simplePayId: string
		) {
			const selectedPage = getters.simplePayPage(simplePayId)

			if (selectedPage && selectedPage.length) {
				const isPageActive =
					selectedPage[0].text.simplePaySettings.isActive
				selectedPage[0].text.simplePaySettings.isActive = isPageActive
					? false
					: true
			}

			const updateObj = {
				id: simplePayId,
				data: selectedPage[0].text
			}

			if (updateObj.data !== null) {
				const resp = await IqProApi.updateSimplePay(
					updateObj.id,
					updateObj.data
				)
				if (resp.statusText.toLowerCase() === 'ok') {
					dispatch('getSimplePayData')
				}
			}
		},
		async showLoadingScreen({ commit }: any) {
			commit('isLoading', true)
		},
		async hideLoadingScreen({ commit }: any) {
			commit('isLoading', false)
		},
		async logIn({ commit }: any, loginCredentials: any) {
			try {
				const resp = await IqProApi.getLoginToken(loginCredentials)
				const timeStamp = Date.now()

				if (resp.data.data.token) {
					commit('login', resp.data.data.token)
					sessionStorage.setItem('isLoggedIn', 'true')
					sessionStorage.setItem(
						'loginToken',
						resp.data.data.loginToken
					)
					sessionStorage.setItem(
						'loginTimeStamp',
						JSON.stringify(timeStamp)
					)
				}
				// local storage can only contain strings, this means that any
				// objects or arrays you wish to store in localStorage must be
				// converted to a JSON string, and then re - formatted when you retrieve them.

				router.push({
					name: 'admin'
				})
			} catch (ex) {
				Sentry.captureException(ex)
				throw new Error('Failed login')
			}
		},
		async checkLoginTimer({ dispatch }: any) {
			const nowStamp = Date.now().valueOf()
			const loggedIn = sessionStorage.getItem('isLoggedIn')
			const loginStamp = new Date(
				Number(sessionStorage.getItem('loginTimeStamp'))
			).valueOf()

			if (loggedIn && loggedIn === 'true') {
				if (loginStamp && nowStamp - loginStamp > 1199999) {
					console.log('Time Elapsed: ' + (nowStamp - loginStamp))
					dispatch('logOut')
				} else {
					sessionStorage.setItem(
						'loginTimeStamp',
						nowStamp.toString()
					)
				}
			}
		},
		async checkExistingLogin({ commit }: any) {
			const loggedIn = sessionStorage.getItem('isLoggedIn')
			const token = sessionStorage.getItem('loginToken')

			if (loggedIn && loggedIn === 'true' && token && token.length) {
				commit('login', token)
			}
		},
		async logOut({ commit }: any) {
			sessionStorage.clear()
			commit('logout')
			router.push({
				name: 'login'
			})
		},
		async setSimplePayUserView({ commit }: any, userView: any) {
			commit('simplePayUserView', userView)
		},
		async isCardholderSurchargeable({ commit }: any, payload: boolean) {
			commit('isCardholderSurchargeable', payload)
		},
		async initSimplePay(
			{ commit, dispatch, state }: any,
			simplePayId: string
		) {
			commit('simplePayId', simplePayId)
			function searchForKey(
				keyName: any,
				searchVal: string,
				array: Array<string>
			) {
				for (let i = 0; i < array.length; i++) {
					if (array[i][keyName] === searchVal) {
						return array[i]
					}
				}
			}

			try {
				const resp = await IqProApi.getSimplePayInitSettings({
					simplePayId: simplePayId,
					distinctId: state.distinctId
				})

				const iq = resp.data.simplePaySettings
				const fp = resp.data.data

				// console.log('IQ: ' + JSON.stringify(iq))
				// console.log('FP: ' + JSON.stringify(fp))

				commit('iqMerchantSettings', iq)
				commit('fpMerchantSettings', fp)

				commit('publicApiKey', fp.api_keys[0].api_key)
				commit(
					'isSurchargingEnabled',
					fp.merchant.flags.surcharge_enabled
				)

				dispatch('setRequiredFields', fp.merchant.requireds)
				dispatch('setCustomFields', fp.custom_fields)

				// payment adjustments
				const processors = fp.processor_settings

				mp.setSimplePaySettings(simplePayId, iq, fp)

				if (
					iq.hasCardForm &&
					iq.cardProcessorId &&
					iq.cardProcessorId.length > 0
				) {
					// find entry in processors that matches iq.cardProcessorId
					const processor = searchForKey(
						'id',
						iq.cardProcessorId,
						processors
					)
					const groupData: GroupProperty = {
						simplePayId: simplePayId,
						processor: processor
					}
					mp.setSimplePayCardProcessor(groupData)
					dispatch('cardAdjustments', processor)
				}

				if (iq.hasAchForm) {
					let achProcessor = null

					if (iq.achProcessorId && iq.achProcessorId.length > 0) {
						// find entry in fp.processors that matches iq.achProcessorId
						achProcessor = searchForKey(
							'id',
							iq.achProcessorId,
							processors
						)
						const groupData: GroupProperty = {
							simplePayId: simplePayId,
							processor: achProcessor
						}
						mp.setSimplePayAchProcessor(groupData)
						dispatch('achAdjustments', achProcessor)
					} else {
						const defaultProcessor = processors.filter(
							(processor: any) => processor.default_ach
						)

						if (defaultProcessor && defaultProcessor.length) {
							achProcessor = defaultProcessor[0]
						} else {
							Sentry.captureMessage(
								'No default ach processor set'
							)
							console.log('No default ach processor set')
						}
					}

					if (achProcessor !== null) {
						dispatch('fetchAchDlAndIdSettings', {
							achProcessor: achProcessor,
							secCode: iq.achProcessing.toLowerCase()
						})
					}
				}
			} catch (ex) {
				router.push({
					name: 'sp-page-not-found'
				})
				mp.pageNotFoundPageViewTrack(simplePayId)
				throw new Error('Could not get resp from gSPIS')
			}
		},
		async setRequiredFields({ commit }, payload) {
			const reqFields: Array<any> = []

			Object.entries(payload).forEach((entry) => {
				const [key, value] = entry
				if (value === 'required') {
					reqFields.push(key)
				}
			})

			commit('iqProReqFields', reqFields)
		},
		async setCustomFields({ commit }, payload) {
			if (!payload) {
				return false
			}
			const cardGroupName = 'simplepaycard'
			const achGroupName = 'simplepayach'
			const vaultFieldName = 'authorizesimplepayvaultrecord'

			const cardFields = payload.filter(
				(field: any) =>
					field.group_name.toLowerCase() === cardGroupName &&
					(field.type === 'text' || field.type === 'select') &&
					field.name.toLowerCase() !== vaultFieldName
			)

			const achFields = payload.filter(
				(field: any) =>
					field.group_name.toLowerCase() === achGroupName &&
					(field.type === 'text' || field.type === 'select') &&
					field.name.toLowerCase() !== vaultFieldName
			)

			const vaultFieldCard = payload.filter(
				(field: any) =>
					field.group_name.toLowerCase() === cardGroupName &&
					field.name.toLowerCase() === vaultFieldName &&
					field.type === 'select'
			)

			const vaultFieldAch = payload.filter(
				(field: any) =>
					field.group_name.toLowerCase() === achGroupName &&
					field.name.toLowerCase() === vaultFieldName &&
					field.type === 'select'
			)

			commit('cardCustomFields', cardFields)
			commit('achCustomFields', achFields)
			commit('vaultCustomFieldCard', vaultFieldCard)
			commit('vaultCustomFieldAch', vaultFieldAch)
		},
		async cardAdjustments({ commit }, processor) {
			const adjType =
				processor.payment_adj_type === 'flat' ||
				processor.payment_adj_type === 'percentage'
					? processor.payment_adj_type
					: ''
			const adjRate = processor.payment_adj_val
				? processor.payment_adj_val
				: 0
			const settings: any = Object.entries(processor.settings).flat()[1]

			commit('paymentAdjustmentsCard', {
				adjType: adjType,
				adjRate: adjRate,
				state:
					settings && settings.merchant_state
						? settings.merchant_state
						: null
			})
		},
		async achAdjustments({ commit }, processor) {
			const adjType =
				processor.payment_adj_type === 'flat' ||
				processor.payment_adj_type === 'percentage'
					? processor.payment_adj_type
					: ''
			const adjRate = processor.payment_adj_val
				? processor.payment_adj_val
				: 0

			commit('paymentAdjustmentsAch', {
				adjType: adjType,
				adjRate: adjRate
			})
		},
		async fetchAchDlAndIdSettings({ commit }: any, payload: any) {
			const terminals = Object.entries(
				payload.achProcessor.settings.sage_ach.terminals
			).flat()
			if (terminals && terminals.length) {
				const achTerminal = terminals.filter((terminal: Terminal) => {
					if (terminal?.sec_code) {
						return (
							terminal.sec_code.toLowerCase() === payload.secCode
						)
					}
				})
				if (achTerminal?.[0]) {
					const dlReq = (achTerminal[0] as any).dl_required
					const idReq = (achTerminal[0] as any).id_required

					commit('achDlAndIdSettings', {
						dlReq: dlReq,
						idReq: idReq
					})
				}
			} else {
				console.log('No terminals found')
			}
		},
		async toggleEmailRequiredOverride(
			{ commit }: any,
			emailRequired: boolean
		) {
			commit('emailRequiredOverrideCard', emailRequired)
		},
		async togglePhoneRequiredOverride(
			{ commit }: any,
			phoneRequired: boolean
		) {
			commit('phoneRequiredOverrideCard', phoneRequired)
		},
		async submitTransaction(
			{ state, commit, dispatch }: any,
			payload: any
		) {
			try {
				const grecaptchaToken = await (window as any).grecaptcha
					.execute(process.env.VUE_APP_GRECAPTCHA_SITE_KEY, {
						action: payload.grecaptchaAction
					})
					.then(async function (resp: any) {
						return resp
					})

				const resp = await IqProApi.postTransaction({
					requestBody: payload.requestBody,
					simplePayId: state.simplePayId,
					grecaptchaToken: grecaptchaToken,
					distinctId: state.distinctId
				}).catch((ex) => {
					throw new Error(ex.message)
				})

				const data = resp.data.data

				const confirmationNumber = data.id
				const transactionAmount = data.amount_authorized
				const respBody = data.response_body

				const respCode =
					data.payment_type === 'ach'
						? respBody.ach.response_code
						: respBody.card.response_code
				const authCode =
					data.payment_type === 'ach'
						? respBody.ach.auth_code
						: respBody.card.auth_code

				if (respCode > 0 && respCode < 200) {
					const settings = state.iqMerchantData.simplePaySettings

					if (settings.webHookUrl && settings.webHookUrl.length) {
						const uri = encodeURI(settings.webHookUrl)

						data.user_id = null
						data.merchant_id = null
						data.processor_id = null
						data.customer_id = null
						data.customer_payment_ID = null
						data.user_id = null
						data.subscription_id = null
						data.referenced_transaction_id = null
						data.response_body.card.id = null
						data.response_body.card.processor_id = null

						try {
							await IqProApi.simplepayWebhookPost(uri, data)
						} catch (ex) {
							Sentry.captureException(ex)
							Sentry.withScope(function (scope) {
								scope.setTag('simplepayid', state.simplePayId)
								scope.setTag('authCode', authCode)
								scope.setTag('responseCode', respCode)
								scope.setTag(
									'confirmationNumber',
									confirmationNumber
								)
								Sentry.captureMessage('wp failed')
							})
							console.log('wp failed', ex)
						}
					}

					if (
						settings.successOverrideUrl &&
						settings.successOverrideUrl.length
					) {
						const uri = encodeURI(settings.successOverrideUrl)
						window.location.replace(uri)

						// if a simplepay page has redirect on it it will add it as a
						// group property
						const groupData: GroupProperty = {
							simplePayId: state.simple_pay_id,
							settings: {
								successOverrideUrl: settings.successOverrideUrl
							}
						}
						mp.setSimplePayRedirect(groupData)

						// Counts if the user is redirected and since it bypasses the success
						// page add it to the success view count
						mp.redirectCountPeopleProp()
						mp.paymentSuccessPeopleCount()
						mp.paymentSuccessAfterRedirectPageViewTrack()
					} else {
						dispatch({
							type: 'setTransactionDetails',
							responseCode: respCode,
							authCode: authCode,
							confirmationNumber: confirmationNumber,
							transactionAmount: transactionAmount,
							timestamp: new Date(Date.now()).toLocaleString()
						})
						commit('clearBanner')

						mp.paymentSuccessPageViewTrack()
						mp.paymentSuccessPeopleCount()

						if (resp.data.data.response_body.card) {
							mp.cardFormTrackTransactionSubmissionSuccess(data)
						}
						if (resp.data.data.response_body.ach) {
							mp.achFormTrackTransactionSubmissionSuccess(data)
						}

						dispatch('setSimplePayUserView', 'success')
						dispatch('hideLoadingScreen')
					}
				} else {
					window.scrollTo(0, 0)

					commit('showBanner', {
						hasBanner: true,
						bannerType: 'error',
						bannerMessage: 'Your payment has been declined.'
					})

					if (resp.data.data.response_body.card) {
						mp.cardFormTrackTransactionSubmissionDecline(data)
					}
					if (resp.data.data.response_body.ach) {
						mp.achFormTrackTransactionSubmissionDecline(data)
					}
					mp.paymentDeclinePeopleCount()
					dispatch('hideLoadingScreen')
				}
			} catch (ex) {
				window.scrollTo(0, 0)

				commit('showBanner', {
					hasBanner: true,
					bannerType: 'error',
					bannerMessage:
						'An error has prevented your payment from processing. Please check that your information is correct and try again.'
				})

				Sentry.withScope(function (scope) {
					scope.setTag('simplepayid', state.simplePayId)
					Sentry.captureMessage(
						'An error has prevented your payment from processing. Please check that your information is correct and try again.'
					)
					Sentry.captureException(ex)
				})
				mp.transactionPreventedFromProcessing(payload)
				mp.paymentProcessingErrorPeopleCount()
				dispatch('hideLoadingScreen')
			}
		},
		async setTransactionDetails(
			{ commit }: any,
			transactionDetails: TransactionDetails
		) {
			commit('responseCode', transactionDetails.responseCode)
			commit('authCode', transactionDetails.authCode)
			commit('confirmationNumber', transactionDetails.confirmationNumber)
			commit('timestamp', transactionDetails.timestamp)
			commit('transactionAmount', transactionDetails.transactionAmount)
		},
		async clearTransactionDetails({ commit }: any) {
			commit('confirmationNumber')
			commit('timestamp')
			commit('transactionAmount')
		}
	},
	getters: {
		simplePayPage: (state: State) => (simplePayId: string) => {
			return state.simplePayData && state.simplePayData.length
				? state.simplePayData.filter(
						(item: any) => item._id === simplePayId
				  )
				: null
		},
		cardFormValidationRules: (state: State) => {
			return {
				firstName: [
					{
						required: true,
						message: 'First name is required',
						trigger: 'blur'
					}
				],
				lastName: [
					{
						required: true,
						message: 'Last name is required',
						trigger: 'blur'
					}
				],
				company: [
					{
						required:
							state.iqMerchantData.simplePaySettings.cardFields
								.companyIsRequired,
						message:
							state.iqMerchantData.simplePaySettings.cardFields
								.companyLabelText + ' is required',
						trigger: 'blur'
					},
					{
						max: 99,
						message: 'Must be less than 100 characters',
						trigger: 'change'
					}
				],
				country: [
					{
						required:
							state.iqMerchantData.simplePaySettings.cardFields
								.addressIsRequired,
						message: 'Country is required',
						trigger: 'blur'
					}
				],
				address: [
					{
						required:
							state.iqMerchantData.simplePaySettings.cardFields
								.addressIsRequired,
						message: 'Address is required',
						trigger: 'blur'
					}
				],
				city: [
					{
						required:
							state.iqMerchantData.simplePaySettings.cardFields
								.addressIsRequired,
						message: 'City is required',
						trigger: 'blur'
					}
				],
				email: [
					{
						required:
							state.iqMerchantData.simplePaySettings.cardFields
								.emailIsRequired ||
							state.iqMerchantData.simplePaySettings.cardFields
								.emailIsRequiredOverride,
						message: 'Email is required',
						trigger: 'blur'
					},
					{
						type: 'email',
						message: 'Email must be valid',
						trigger: 'blur'
					}
				],
				receiptEmail: [
					{
						type: 'email',
						message: 'Email must be valid',
						trigger: 'blur'
					}
				],
				phone: [
					{
						required:
							state.iqMerchantData.simplePaySettings.cardFields
								.phoneIsRequired ||
							state.iqMerchantData.simplePaySettings.cardFields
								.phoneIsRequiredOverride,
						message: `Phone number is required`,
						trigger: 'blur'
					},
					{
						pattern: /^[2-9][0-9]{9}$/,
						message: 'Phone number must be ten digits',
						trigger: 'blur'
					}
				],
				orderId: [
					{
						required:
							state.iqMerchantData.simplePaySettings.cardFields
								.orderIdIsRequired,
						message:
							state.iqMerchantData.simplePaySettings.cardFields
								.orderIdLabelText + ' is required',
						trigger: 'blur'
					},
					{
						max: 16,
						message: 'Cannot exceed 16 characters',
						trigger: 'change'
					}
				],
				poNumber: [
					{
						required:
							state.iqMerchantData.simplePaySettings.cardFields
								.poNumberIsRequired,
						message:
							state.iqMerchantData.simplePaySettings.cardFields
								.poNumberLabelText + ' is required',
						trigger: 'blur'
					},
					{
						max: 16,
						message: 'Cannot exceed 16 characters',
						trigger: 'change'
					}
				],
				amount: [
					{
						required: true,
						message: 'Amount is required',
						trigger: 'blur'
					},
					{
						pattern: /^[0-9]+\.[0-9][0-9]$/,
						message: 'Amount must be in 0.00 format',
						trigger: 'blur'
					}
				]
			}
		},
		achFormValidationRules: (state: State) => {
			return {
				firstName: [
					{
						required: true,
						message: 'First name is required',
						trigger: 'blur'
					}
				],
				lastName: [
					{
						required: true,
						message: 'Last name is required',
						trigger: 'blur'
					}
				],
				company: [
					{
						required:
							state.iqMerchantData.simplePaySettings.achFields
								.companyIsRequired,
						message:
							state.iqMerchantData.simplePaySettings.achFields
								.companyLabelText + ' is required',
						trigger: 'blur'
					},
					{
						max: 99,
						message: 'Must be less than 100 characters',
						trigger: 'change'
					}
				],
				address: [
					{
						required: true,
						message: 'Address is required',
						trigger: 'blur'
					}
				],
				country: [
					{
						required: true,
						message: 'Country is required',
						trigger: 'blur'
					}
				],
				city: [
					{
						required: true,
						message: 'City is required',
						trigger: 'blur'
					}
				],

				email: [
					{
						required: true,
						message: 'Email is required',
						trigger: 'blur'
					},
					{
						type: 'email',
						message: 'Email must be valid',
						trigger: 'blur'
					}
				],
				receiptEmail: [
					{
						type: 'email',
						message: 'Email must be valid',
						trigger: 'blur'
					}
				],
				dlState: [
					{
						required:
							state.iqMerchantData.simplePaySettings.achFields
								.driversLicenseIsIncluded,
						message: "Driver's license state is required",
						trigger: 'blur'
					}
				],
				dlNumber: [
					{
						required:
							state.iqMerchantData.simplePaySettings.achFields
								.driversLicenseIsIncluded,
						message: "Driver's license number is required",
						trigger: 'blur'
					}
				],
				dob: [
					{
						required:
							state.iqMerchantData.simplePaySettings.achFields
								.dobSSNIsIncluded,
						message: 'Date of birth is required',
						trigger: 'blur'
					}
				],
				ssn: [
					{
						required:
							state.iqMerchantData.simplePaySettings.achFields
								.dobSSNIsIncluded,
						message: 'Last four digits of SSN are required',
						trigger: 'blur'
					},
					{
						pattern: /^[0-9]{4}$/,
						message: 'Provide only last four digits of SSN',
						trigger: 'blur'
					}
				],
				phone: [
					{
						required: true,
						message: 'Phone is required',
						trigger: 'blur'
					},
					{
						pattern: /^[2-9][0-9]{9}$/,
						message: 'Phone number must be ten digits',
						trigger: 'blur'
					}
				],
				orderId: [
					{
						required:
							state.iqMerchantData.simplePaySettings.achFields
								.orderIdIsRequired,
						message:
							state.iqMerchantData.simplePaySettings.achFields
								.orderIdLabelText + ' is required',
						trigger: 'blur'
					},
					{
						max: 16,
						message: 'Cannot exceed 16 characters',
						trigger: 'change'
					}
				],
				poNumber: [
					{
						required:
							state.iqMerchantData.simplePaySettings.achFields
								.poNumberIsRequired,
						message:
							state.iqMerchantData.simplePaySettings.achFields
								.poNumberLabelText + ' is required',
						trigger: 'blur'
					},
					{
						max: 16,
						message: 'Cannot exceed 16 characters',
						trigger: 'change'
					}
				],
				amount: [
					{
						required: true,
						message: 'Amount is required',
						trigger: 'blur'
					},
					{
						pattern: /^[0-9]+\.[0-9][0-9]$/,
						message: 'Amount must be in 0.00 format',
						trigger: 'blur'
					}
				]
			}
		}
	}
})

export function useStore() {
	return baseUseStore(key)
}
